import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { EquipmentModelDictionaryService } from './equipment-model-dictionary.service';
import { combineLatest, map, scan, shareReplay, startWith, tap } from 'rxjs';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { MultiselectComponent } from '@ui-lib/components';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mon-equipment-model-selector',
  imports: [FormsModule, TranslateModule, MultiselectComponent, ReactiveFormsModule, AsyncPipe],
  templateUrl: './equipment-model-selector.component.html',
  styleUrl: './equipment-model-selector.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EquipmentModelDictionaryService],
})
export class EquipmentModelSelectorComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  protected readonly modelDictionaryService = inject(EquipmentModelDictionaryService);

  @Input()
  public controlEntity: FormControl<string[]> = new FormControl([], { nonNullable: true });

  @Input()
  public disabled = false;

  @Output()
  public readonly selectionChange = new EventEmitter<IDictionaryItem<string>[]>();

  private loadedCount = 0;

  private readonly idNameMap: Map<string, string> = new Map<string, string>();

  protected readonly models$ = this.modelDictionaryService.source$.pipe(
    scan(
      (
        acc: IDictionaryItem<string>[],
        newbies: IDictionaryItem<string>[],
      ): IDictionaryItem<string>[] => [...acc, ...newbies],
      [],
    ),
    map((items: IDictionaryItem<string>[]): IDictionaryItem<string>[] => {
      const seenIds = new Set<string>();

      return items.filter((item: IDictionaryItem<string>): boolean => {
        if (!item.id) return false;

        if (seenIds.has(item.id)) {
          return false;
        }

        seenIds.add(item.id);

        return true;
      });
    }),
    tap((items: IDictionaryItem<string>[]): void => {
      this.loadedCount = items.length;

      this.idNameMap.clear();

      items.forEach((item: IDictionaryItem<string>): void => {
        this.idNameMap.set(item.id, item.name);
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public ngOnInit(): void {
    combineLatest([
      this.controlEntity.valueChanges.pipe(startWith(this.controlEntity.value)),
      this.models$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([ids]): void => {
        this.selectionChange.emit(
          ids.map(
            (id: string): IDictionaryItem<string> => ({
              id,
              name: this.idNameMap.get(id) ?? '-',
            }),
          ),
        );
      });
  }

  public loadMore(): void {
    if (this.loadedCount < this.modelDictionaryService.totalCount()) {
      this.modelDictionaryService.loadMore();
    }
  }
}
